function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }

    if(value < 2) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.disabled = true;
    } else {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.disabled = false;
    }

    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}


class SwiperCarousel extends HTMLElement {
  constructor() {
    super()
    this.init()
  }

  connectedCallback() {
    this.init()
  }

  async init () {
    const options = this.dataset.swiper ? JSON.parse(this.dataset.swiper) : {}
    console.log(options, 'options')
    let element = this
    if (!this.classList.contains('swiper')) {
      element = this.querySelector('.swiper');
    }

    // Check if this is a main or thumbnail swiper based on data attribute
    const isMainSwiper = this.dataset.type === 'main';
    const isThumbSwiper = this.dataset.type === 'thumb';
    const swiperId = this.dataset.swiperId;

    if (isMainSwiper) {
      const thumbSwiper = await this.getThumbSwiper(swiperId);
      this.swiper = new Swiper(element, {
        // If we need pagination
        pagination: {
          el: this.querySelector(".swiper-pagination"),
          clickable: true,
        },
  
        // Navigation arrows
        navigation: {
          nextEl: this.querySelector(".swiper-button-next"),
          prevEl: this.querySelector(".swiper-button-prev"),
        },
        
        thumbs: {
          swiper: thumbSwiper,
        },
  
        // modules: [Navigation, Pagination, Thumbs],
  
        ...options
      });
    } else {
      if (isThumbSwiper) {
        // Initialize thumbnail Swiper
        this.swiper = new Swiper(element, {
          watchSlidesProgress: true,
          // modules: [Thumbs],
          ...options,
        });
      } else {
        console.log("ekse else")
        this.swiper = new Swiper(element, {
          // If we need pagination
          pagination: {
            el: this.querySelector(".swiper-pagination"),
            clickable: true,
          },
    
          // Navigation arrows
          navigation: {
            nextEl: this.querySelector(".swiper-button-next"),
            prevEl: this.querySelector(".swiper-button-prev"),
          },
    
          // modules: [Navigation, Pagination],
    
          ...options
        });
      }
    }

    if (options.loop) {
      this.swiper.on('slideChange', () => {
        const next = document.querySelector('.swiper-button-next')
        if (next && next.classList.contains('swiper-button-disabled')) {
          next.classList.remove('swiper-button-disabled')
        }

        const prev = document.querySelector('.swiper-button-prev')
        if (prev && prev.classList.contains('swiper-button-disabled')) {
          prev.classList.remove('swiper-button-disabled')
        }
      })
    }
  }

  // Function to find and wait for the thumbnail swiper instance
  getThumbSwiper(swiperId) {
    return new Promise((resolve) => {
      const checkThumbSwiper = () => {
        const thumbElement = document.querySelector(`swiper-carousel[data-type="thumb"][data-swiper-id="${swiperId}"]`);
        if (thumbElement && thumbElement.swiper) {
          resolve(thumbElement.swiper);
        } else {
          setTimeout(checkThumbSwiper, 100); // Retry after 100ms if thumbnail swiper is not ready
        }
      };
      checkThumbSwiper();
    });
  }
}

if (!customElements.get('swiper-carousel'))
  customElements.define('swiper-carousel', SwiperCarousel)

document.addEventListener('DOMContentLoaded', () => {
  const dataTabSection = document.querySelectorAll('[data-tab-section]')

  if(dataTabSection.length) {
    dataTabSection.forEach(tabSection => {
      const tabs = tabSection.querySelectorAll('[data-tab]');
      const grids = tabSection.querySelectorAll('[data-block]');

      grids.forEach((g, index) => {
        if (index !== 0) {
            g.classList.add('hidden');
          }
      });

      tabs.forEach(tab => {
        tab.addEventListener('click', (e,index) => {
          tabs.forEach(t => t.classList.remove('active_tab')); // Optional: Add active styling
          grids.forEach(g => g.classList.add('hidden'));  // Hide all grids
    
          tab.classList.add('active_tab'); // Optional: Add active styling
          tabSection.querySelector(`[data-block="${tab.dataset.tab}"]`).classList.remove('hidden'); // Show target grid
        });
      });
    })
  }
});

class sideDrawerElement extends HTMLElement {
  constructor() {
    super();
    this.initSideDrawer()
  }

  connectedCallback() {
    this.initSideDrawer()
  }

  initSideDrawer() {
    const drawerLink = this.querySelectorAll('[data-drawer-link]');
    const drawerClose = this.querySelectorAll('[data-close-drawer]');
    const sideDrawerEl = document.querySelector('side-drawer');
  
    if(drawerLink.length) {
      drawerLink.forEach(function(link){
        link.addEventListener('click',(e)=>{
          e.preventDefault();
          const sideDrawer = e.target.closest('[drawer-block]');
         
          if(sideDrawer) {
            const drawerHTML = sideDrawer.querySelector('modal-drawer');
  
            if(drawerHTML) {
              sideDrawerEl.innerHTML = drawerHTML.innerHTML;
              sideDrawerEl.classList.add('active')
              document.body.classList.add('overflowH');
            }
  
            if(drawerClose.length) {
              sideDrawerEl.querySelectorAll('[data-close-drawer]').forEach(function(closeBtn){
                closeBtn.addEventListener('click',(btn)=>{
                  // sideDrawerEl.innerHTML = '';
                  sideDrawerEl.classList.remove('active');
                  document.body.classList.remove('overflowH')
                })
              })
            }

            // selling plan readd
            const sellingPlanEl = sideDrawerEl.querySelectorAll('input.selling_plan-input');
            const sellingInputEl = sideDrawerEl.querySelector('[data-selling-ids]');
            const atcButton = sideDrawerEl.querySelector('button.product-form__submit')
            const sellingIds =  sideDrawerEl.querySelector('[data-selling-ids]')
    
            if(sellingPlanEl.length && sellingInputEl){
              sellingPlanEl.forEach(input => {
                input.addEventListener('change',(evt)=>{
                  // remove active class first and add in current active input
                  sideDrawerEl.querySelectorAll('[data-js-widget]').forEach((widget)=>widget.classList.remove('active'))
                  evt.target.closest('[data-js-widget]').classList.add('active')
    
                  // disable selling plan input if one time selected
                  if(evt.target.closest('[data-js-widget]').hasAttribute('data-option-onetime')) {
                    sellingInputEl.querySelector('input').disabled = true;
                    atcButton.querySelector('[data-atc-price]').innerHTML = '- ' + evt.target.closest('[data-js-widget]').querySelector('[data-onetime-price]').innerHTML
                  } else {
                    sellingInputEl.querySelector('input').disabled = false;
                    sellingIds.querySelector('input').value = evt.target.closest('[data-js-widget]').getAttribute('data-selling-plan-id')
                    atcButton.querySelector('[data-atc-price]').innerHTML = '- ' + evt.target.closest('[data-js-widget]').querySelector('[data-save-price]').innerHTML
                  }
                })
              })
            }

            // tab switching readd
            if(sideDrawerEl.classList.contains('active')) {
              const dataTabSectionRe = document.querySelectorAll('[data-tab-section]')

              if(dataTabSectionRe.length) {
                dataTabSectionRe.forEach(tabSection => {
                  const tabsRe = tabSection.querySelectorAll('[data-tab]');
                  const gridsRe = tabSection.querySelectorAll('[data-block]');
  
                  tabsRe.forEach(tab => {
                    tab.addEventListener('click', (e,index) => {
                      tabsRe.forEach(t => t.classList.remove('active_tab')); // Optional: Add active styling
                      gridsRe.forEach(g => g.classList.add('hidden'));  // Hide all grids
                
                      tab.classList.add('active_tab'); // Optional: Add active styling
                      tabSection.querySelector(`[data-block="${tab.dataset.tab}"]`).classList.remove('hidden'); // Show target grid
                    });
                  });
                })
              }
            }

          }
        })
      })
    }
  }
}

customElements.define('side-drawer-element', sideDrawerElement);


document.addEventListener("DOMContentLoaded", (event) => {
  const menuLinksDesktop = document.querySelectorAll('.desktop_menu_link');
  menuLinksDesktop.forEach(function(menu_link){
    menu_link.addEventListener('click',(link)=>{
      if(link.currentTarget.getAttribute('href') == '#') {
        console.log('inn')
        link.preventDefault();
      } else {
        return;
      }

      const megaMenuBlock = link.currentTarget.nextElementSibling;
      console.log(megaMenuBlock)

      if(megaMenuBlock) {
        if(link.currentTarget.getAttribute('data-link-title') == megaMenuBlock.getAttribute('data-block-handle')) {
          document.body.classList.toggle('overflow-hidden')
          link.currentTarget.classList.toggle('active_link')
          megaMenuBlock.classList.toggle('active_menu')
        }
      }
    })
  });


  // table hover popup
  const popupHoverEl = document.querySelectorAll('.popup_hover');
  const mobilePopupEl = document.querySelectorAll('.mobile_popup_el')

  if(popupHoverEl.length) {
    popupHoverEl.forEach((el)=>{
      el.addEventListener('mouseover',(e)=>{
        console.log(e)
        if(e.currentTarget.querySelector('.mini_popup_text')) {
          e.currentTarget.querySelector('.mini_popup_text').style.display = 'block'
        }
      }) 
      el.addEventListener('mouseout',(e)=>{
        if(e.currentTarget.querySelector('.mini_popup_text')) {
          e.currentTarget.querySelector('.mini_popup_text').style.display = 'none'
        }
      }) 
    })
  }

  if(mobilePopupEl.length) {
    mobilePopupEl.forEach((el)=>{
      el.addEventListener('click',(e)=>{
        e.currentTarget.querySelector('.mini_popup_text').classList.toggle('popup-hidden')
      }) 
    })
  }

  const addToCartButton = document.querySelector('.buy-buttons'); // Replace with actual selector
  const stickyBar = document.querySelector('.sticky_atc-bar'); // Replace with actual selector

  if (addToCartButton && stickyBar) {
      // Get the position of the "Add to Cart" button
      const addToCartButtonPosition = addToCartButton.getBoundingClientRect().top + window.scrollY;

      window.addEventListener('scroll', () => {
          // Check if the scroll position has passed the "Add to Cart" button
          if (window.scrollY > addToCartButtonPosition) {
              stickyBar.style.display = 'block';
              document.body.classList.add('chat-button');
          } else {
              stickyBar.style.display = 'none';
              document.body.classList.remove('chat-button');
          }
      }); 
  } else {
      console.warn("Could not find the 'Add to Cart' button or sticky bar element.");
  }


  // sticky options pdp js subscription 
  const stickyOptionsJsEl = document.querySelectorAll('.sticky_options-js');

  if(stickyOptionsJsEl.length) {
    stickyOptionsJsEl.forEach(function(stickyEl){
      const inputRadioEl = stickyEl.querySelectorAll('input[type="radio"]');
      inputRadioEl.forEach(function(input){
        input.addEventListener('change',function(e){
          e.target.closest('[data-subscription-widget]').querySelectorAll('[data-js-widget]').forEach((widget)=>widget.classList.remove('active'))
          e.target.closest('[data-js-widget]').classList.add('active');

          // disable selling plan input if one time selected
          const stickySellingIdsEl = e.target.closest('.sticky-inner').querySelector('[data-selling-ids]');
          const stickyAtcButton = e.target.closest('.sticky-inner').querySelector('.product-form__submit');
          if(e.target.closest('[data-js-widget]').hasAttribute('data-option-onetime')) {
            stickySellingIdsEl.querySelector('input').disabled = true;
            stickyAtcButton.querySelector('[data-atc-price]').innerHTML = '- ' + e.target.closest('[data-js-widget]').querySelector('[data-onetime-price]').innerHTML
          } else {
            stickySellingIdsEl.querySelector('input').disabled = false;
            stickySellingIdsEl.querySelector('input').value = e.target.closest('[data-js-widget]').getAttribute('data-selling-plan-id')
            stickyAtcButton.querySelector('[data-atc-price]').innerHTML = '- ' + e.target.closest('[data-js-widget]').querySelector('[data-save-price]').innerHTML
          }

          // trigger main first fold option
          const sellingWidgetFirstFold = document.querySelector('.selling__first-fold-widget');
          const stickyCurrentAttributeEl = e.target.closest('[data-js-widget]');
          const dynamicAttrName = [...stickyCurrentAttributeEl.attributes].find(attr => attr.name.startsWith("data-option"))?.name;
          console.log(dynamicAttrName);
          if(sellingWidgetFirstFold && dynamicAttrName) {
            sellingWidgetFirstFold.querySelector(`[${dynamicAttrName}]`).querySelector('input[type="radio"]').checked = true;
            sellingWidgetFirstFold.querySelector(`[${dynamicAttrName}]`).querySelector('input[type="radio"]').dispatchEvent(new Event("change", { bubbles: true }));
          }
        })
      })
    })
  }

  const drawerObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1 && node.id === 'cfm-side-drawer') {
          console.log('Drawer added');
  
          // Start a second observer scoped to the drawer
          const ingredientObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              mutation.addedNodes.forEach((subNode) => {
                // Check if the element or any of its children are ingredient containers
                if (subNode.nodeType === 1) {
                  const containers = subNode.matches?.('.cfm-ingredient-componet-container')
                    ? [subNode]
                    : subNode.querySelectorAll?.('.cfm-ingredient-componet-container');
  
                  if (containers?.length) {
                    containers.forEach((el) => {
                      if (el.getAttribute('aria-expanded') === 'true') {
                        // Simulate Enter key to collapse
                        const event = new KeyboardEvent('keydown', {
                          key: 'Enter',
                          keyCode: 13,
                          which: 13,
                          bubbles: true,
                          cancelable: true,
                        });
                        el.dispatchEvent(event);
                        console.log('Collapsed ingredient section via keydown');
                      }
                    });
  
                    // Stop observing once we handled it
                    ingredientObserver.disconnect();
                  }
                }
              });
            });
          });
  
          ingredientObserver.observe(node, { childList: true, subtree: true });
        }
      });
    });
  });
  
  drawerObserver.observe(document.body, { childList: true, subtree: false });
  
});



window.theme = window.theme || {};
theme.initWhenVisible = function(options) {
  const threshold = options.threshold ? options.threshold : 0;

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (typeof options.callback === 'function') {
          options.callback();
          observer.unobserve(entry.target);
        }
      }
    });
  }, {rootMargin: `0px 0px ${threshold}px 0px`});

  observer.observe(options.element);
};


class announcementScroll extends HTMLElement {
  constructor() {
    super();

    this.config = {
      moveTime: parseFloat(this.dataset.speed), // 100px going to move for
      space: 100, // 100px
    };

    this.promotion = this.querySelector('.promotion');


    theme.initWhenVisible({
      element: this,
      callback: () => {
          this.init();
      },
      threshold: 600
    });
  }

  

  init() {
    if (this.childElementCount === 1) {
      this.promotion.classList.add('promotion--animated');

      for (let index = 0; index < 2; index++) {
        this.clone = this.promotion.cloneNode(true);
        this.clone.setAttribute('aria-hidden', true);
        this.appendChild(this.clone);

        const imageWrapper = this.clone.querySelector('.media-wrapper');
        if (imageWrapper) {
          imageWrapper.classList.remove('loading');
        }
      }

      const animationTimeFrame = (this.promotion.clientWidth / this.config.space) * this.config.moveTime;
      this.style.setProperty('--duration', `${animationTimeFrame}s`);

      // pause when out of view
      const observer = new IntersectionObserver((entries, _observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.scrollingPlay();
          } else {
            this.scrollingPause();
          }
        });
      }, {rootMargin: '0px 0px 500px 0px'});

      observer.observe(this);
    }
  }

  scrollingPlay() {
    this.classList.remove('scrolling-promotion--paused');
  }

  scrollingPause() {
    this.classList.add('scrolling-promotion--paused');
  }

  reset() {
    // Optional: reset or remove animation if the screen width is more than 768px
    this.promotion.classList.remove('promotion--animated', 'scrolling-promotion--paused');
    this.querySelectorAll('[aria-hidden=true]').forEach(clone => clone.remove());
  }
}

customElements.define('scrolling-promotion', announcementScroll);


const attachButtonListeners = () => {
  const buttons = document.querySelectorAll('.cfm-ingredient-componet-container');
  buttons.forEach(button => {
    if (!button.dataset.listenerAdded) {
      button.addEventListener('click', (e) => {
        if(e.target.classList.contains('component-name')) {
        setTimeout(() => {
          console.log(e.target, 'Button clicked:', button, button.offsetTop);
            var popup = document.querySelector('.cfm-side-drawer-content');
            popup.scrollTo({
              top: button.offsetTop - 100,
              behavior: "smooth"
            });
          }, 100);
        }
      });
    }
  });
};

const observer = new MutationObserver(() => {
  attachButtonListeners();
});

// Start observing the body for added child elements (AJAX content)
observer.observe(document.body, {
  childList: true,
  subtree: true
});

// Run once initially in case some buttons are already present
attachButtonListeners();

class AccordionItemMain extends HTMLElement {
  constructor() {
    super();
    this.trigger = null;
    this.content = null;
    this.isInitialized = false;
    this.section = this.closest('section') || this.closest('side-drawer') || null;

  }

  connectedCallback() {
    if (!this.isInitialized) {
      this.init();
      this.isInitialized = true;
    }
  }

  init() {

    // Find the trigger and content elements within this accordion item
    this.trigger = this.querySelector('.accordion-trigger-button');
    this.content = this.trigger ? this.trigger.nextElementSibling : null;

    if (!this.trigger || !this.content) {
      console.warn('AccordionItemMain: Missing trigger or content element');
      return;
    }

    // Add click event listener to the trigger
    this.trigger.addEventListener('click', this.handleTriggerClick.bind(this));
  }

  handleTriggerClick() {
    const isOpen = this.trigger.hasAttribute('open');

    // Close all other accordion items
    this.closeOtherAccordions();

    if (!isOpen) {
      this.openAccordion();
    } else {
      this.closeAccordion();
    }
  }

  closeOtherAccordions() {
    
    console.log(this.section, 'this.section')
    const allAccordions = this.section.querySelectorAll('accordion-item-main')

    allAccordions.forEach(accordion => {
      if (accordion !== this) {
        const otherTrigger = accordion.querySelector('.accordion-trigger-button');
        const otherContent = otherTrigger ? otherTrigger.nextElementSibling : null;
        
        if (otherTrigger && otherContent && otherTrigger.hasAttribute('open')) {
          // Animate close of other accordion
          otherContent.animate(
            [
              { height: otherContent.scrollHeight + 'px', opacity: 1 },
              { height: '0px', opacity: 0 }
            ],
            { duration: 300, easing: 'ease' }
          ).onfinish = () => {
            otherContent.style.display = 'none';
            otherTrigger.removeAttribute('open');
          };
        }
      }
    });
  }

  openAccordion() {
    // Open current accordion
    this.content.style.display = 'block';
    const height = this.content.scrollHeight;

    this.content.animate(
      [
        { height: '0px', opacity: 0 },
        { height: height + 'px', opacity: 1 }
      ],
      { duration: 300, easing: 'ease' }
      
    ).onfinish = () => {
      this.content.style.height = 'auto';
      this.trigger.setAttribute('open', '');
      setTimeout(() => {
        var popup = this.closest('.drawer_inner');
        popup?.scrollTo({
          top: this.trigger.offsetTop - 100,
          behavior: "smooth"
        });
      }, 100);
    };
  }

  closeAccordion() {
    // Close current accordion
    this.content.animate(
      [
        { height: this.content.scrollHeight + 'px', opacity: 1 },
        { height: '0px', opacity: 0 }
      ],
      { duration: 300, easing: 'ease' }
    ).onfinish = () => {
      this.content.style.display = 'none';
      this.trigger.removeAttribute('open');
    };
  }

  disconnectedCallback() {
    // Clean up event listeners when the component is removed
    if (this.trigger) {
      this.trigger.removeEventListener('click', this.handleTriggerClick);
    }
  }
}

// Register the custom element
customElements.define('accordion-item-main', AccordionItemMain);

class DrawerTabs extends HTMLElement {
  constructor() {
    super();
    this.activeTransitions = new Set(); // Track active transitions
  }

  connectedCallback() {
    console.log('DrawerTabs connected');
    const links = this.querySelectorAll('li a');
    
    // Set first link as active by default if no active link exists
    if (!this.querySelector('li a.active') && links.length > 0) {
      links[0].classList.add('active');
    }
    
    links.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Don't proceed if this link is already active
        if (link.classList.contains('active')) {
          return;
        }
        
        const target = link.getAttribute('data-target');
        const allPanels = document.querySelectorAll('[data-tab-content]');
        const targetPanel = document.querySelector(`[data-tab-content="${target}"]`);
        
        if (targetPanel) {
          // Update active states for links
          this.updateActiveLinkStates(link);
          
          // Handle panel transitions
          this.handlePanelTransitions(allPanels, targetPanel);
        }
      });
    });
  }
  
  updateActiveLinkStates(activeLink) {
    // Remove active class from all links
    const allLinks = this.querySelectorAll('li a');
    allLinks.forEach(link => {
      link.classList.remove('active');
    });
    
    // Add active class to clicked link
    activeLink.classList.add('active');
  }
  
  async handlePanelTransitions(allPanels, targetPanel) {
    // Cancel any ongoing transitions
    this.activeTransitions.clear();
    
    // Find currently visible panel
    const visiblePanel = Array.from(allPanels).find(panel => 
      !panel.classList.contains('hidden')
    );
    
    if (visiblePanel && visiblePanel !== targetPanel) {
      // Create a transition promise for the hiding animation
      const hidePromise = new Promise((resolve) => {
        const transitionId = Math.random();
        this.activeTransitions.add(transitionId);
        
        const hideAnimation = visiblePanel.animate(
          [
            { opacity: 1, transform: 'translateY(0px)' },
            { opacity: 0, transform: 'translateY(-20px)' }
          ],
          { 
            duration: 200,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            fill: 'forwards'
          }
        );
        
        hideAnimation.onfinish = () => {
          if (this.activeTransitions.has(transitionId)) {
            visiblePanel.classList.add('hidden');
            visiblePanel.style.opacity = '';
            visiblePanel.style.transform = '';
            resolve();
          }
        };
      });
      
      // Wait for hide animation to complete before showing new panel
      await hidePromise;
    }
    
    // Show target panel with animation from top
    if (targetPanel.classList.contains('hidden')) {
      targetPanel.classList.remove('hidden');
      
      // Set initial state
      targetPanel.style.opacity = '0';
      targetPanel.style.transform = 'translateY(-20px)';
      
      // Force reflow to ensure initial state is applied
      targetPanel.offsetHeight;
      
      // Animate to final state
      const showAnimation = targetPanel.animate(
        [
          { opacity: 0, transform: 'translateY(-20px)' },
          { opacity: 1, transform: 'translateY(0px)' }
        ],
        { 
          duration: 300,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
          fill: 'forwards'
        }
      );
      
      showAnimation.onfinish = () => {
        // Clean up inline styles
        targetPanel.style.opacity = '';
        targetPanel.style.transform = '';
      };
    }
  }
}

customElements.define('drawer-tabs', DrawerTabs);

if ("IntersectionObserver" in window) {
  let counterObserver = new IntersectionObserver(function (entries, observer) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        let counter = entry.target;
        let target = parseInt(counter.dataset.counterCountup);
        let duration = 2000; // 2 seconds
        let startTime = null;

        function updateCounter(timestamp) {
          if (!startTime) startTime = timestamp;
          let elapsed = timestamp - startTime;
          let progress = Math.min(elapsed / duration, 1);
          counter.innerText = Math.floor(progress * target);

          if (progress < 1) {
            requestAnimationFrame(updateCounter);
          } else {
            counter.innerText = target; // Ensure exact final value
          }
        }

        requestAnimationFrame(updateCounter);
        counterObserver.unobserve(counter);
      }
    });
  });

  let counters = document.querySelectorAll("[data-counter-countup]");
  counters.forEach(function (counter) {
    counterObserver.observe(counter);
  });
}



  document.querySelectorAll('.accordion-link').forEach(link => {
    link.addEventListener('click', function(event) {
      event.preventDefault();
      const targetId = this.getAttribute('href');
      const targetElement = document.querySelector(targetId);
      console.log(targetId, 'targetId', targetElement, 'targetElement');
      console.log(targetElement, 'targetElement');
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    });
  });